
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TablePagination,
  Typography,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import { Snackbar, Alert } from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

const TopUpHistoryModal = ({ open, onClose }) => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState("");
  const [years, setYears] = useState([]);
  const [month, setMonth] = useState("");
  const [quarter, setQuarter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // ✅ Invoice states
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewError, setInvoicePreviewError] = useState("");
  const [currentTopupId, setCurrentTopupId] = useState(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);

  const handleViewInvoice = async (topup_id) => {
    setInvoiceLoadingId(topup_id);
    setCurrentTopupId(topup_id);
    setInvoicePreviewOpen(true);
    setInvoicePreviewLoading(true);
    setInvoicePreviewError("");
    setInvoicePreviewUrl("");

    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/get_invoices_topup/${topup_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const url = response.data?.invoice_url;
      if (url) {
        setInvoicePreviewUrl(url);
      } else {
        setInvoicePreviewError("No invoice available.");
      }
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to fetch invoice";

      setInvoicePreviewError(message);
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setInvoiceLoadingId(null);
      setInvoicePreviewLoading(false);
    }
  };


  const handleDownloadInvoice = async () => {
  if (!invoicePreviewUrl) return;

  try {
    const response = await axios.get(invoicePreviewUrl, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `TopUp_Invoice_${currentTopupId || ""}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Error downloading invoice:", err);
  }
};
  const fetchTopups = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/accounts/view_topups`, {
        params: {
          year: year || undefined,
          month: month || undefined,
          quarter: quarter || undefined,
          page: page + 1,
          page_size: pageSize,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      setAllRows(res.data.data || []);
      setTotalCount(res.data.total_count || 0);
      setYears(res.data.years || []);
    } catch (error) {
      console.error("Error fetching topup history:", error);
      setAllRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchTopups();
  }, [open, year, month, quarter, page, pageSize]);

  const filteredRows = useMemo(() => {
    const q = searchInput.toLowerCase().trim();
    if (!q) return allRows;

    return allRows.filter((row) =>
      [row.institute_name, row.registration_limit, row.topup_date]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [allRows, searchInput]);

  const handleClose = () => {
    onClose();
    setYear("");
    setMonth("");
    setQuarter("");
    setPage(0);
    setPageSize(5);
    setSearchInput("");
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      {/* ===== MAIN MODAL ===== */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">Top Up History</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Filters */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search institute..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Select value={year} onChange={(e) => setYear(e.target.value)} displayEmpty size="small">
              <MenuItem value="">Year</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>

            <Select value={month} onChange={(e) => setMonth(e.target.value)} displayEmpty size="small">
              <MenuItem value="">Month</MenuItem>
              {[...Array(12)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
              ))}
            </Select>

            <Select value={quarter} onChange={(e) => setQuarter(e.target.value)} displayEmpty size="small">
              <MenuItem value="">Quarter</MenuItem>
              {[1, 2, 3, 4].map((q) => (
                <MenuItem key={q} value={q}>Q{q}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Table */}
          <TableContainer 
            component={Paper}
            sx={{
              maxHeight: "400px",   // controls scroll height
              overflowY: "auto",
            }}
          >

            <Table stickyHeader>
              <TableHead>
              <TableRow sx={{ backgroundColor: "#1053e3ff" }}>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Sl.No
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Institute
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Limit
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Date
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Invoice
                </TableCell>
              </TableRow>
            </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{page * pageSize + index + 1}</TableCell>
                    <TableCell>{row.institute_name}</TableCell>
                    <TableCell>{row.registration_limit}</TableCell>
                    <TableCell>{row.topup_date}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleViewInvoice(row.topup_id)}>
                        {invoiceLoadingId === row.topup_id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ===== INVOICE PREVIEW ===== */}
  <Dialog
  open={invoicePreviewOpen}
  onClose={() => setInvoicePreviewOpen(false)}
  fullWidth
  maxWidth="lg"
  PaperProps={{
    sx: {
      borderRadius: "8px",
      height: "90vh",
    },
  }}
>
  <DialogTitle
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #E5E7EB",
      pb: 2,
    }}
  >
    <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1F1F1F" }}>
      Invoice Preview {currentTopupId && `- ${currentTopupId}`}
    </Typography>
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Button
        variant="contained"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadInvoice}
        disabled={!invoicePreviewUrl || invoicePreviewLoading}
        sx={{
          backgroundColor: "#10B981",
          "&:hover": { backgroundColor: "#059669" },
          "&.Mui-disabled": {
            backgroundColor: "#9CA3AF",
            color: "#FFFFFF",
          },
          textTransform: "none",
          fontSize: "12px",
          fontWeight: 500,
          boxShadow: "none",
        }}
      >
        Download
      </Button>
      <IconButton
        onClick={() => setInvoicePreviewOpen(false)}
        size="small"
        sx={{
          color: "#6B7280",
          "&:hover": { backgroundColor: "#F3F4F6" },
        }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent
    sx={{
      p: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#F9FAFB",
      height: "100%",
    }}
  >
    {invoicePreviewLoading ? (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
          Loading invoice...
        </Typography>
      </Box>
    ) : invoicePreviewError ? (
      <Typography sx={{ fontSize: "13px", color: "#EF4444" }}>
        {invoicePreviewError}
      </Typography>
    ) : invoicePreviewUrl ? (
      <iframe
        src={invoicePreviewUrl}
        style={{ width: "100%", height: "100%", border: "none" }}
        title="Invoice"
      />
    ) : (
      <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
        No invoice available
      </Typography>
    )}
  </DialogContent>
</Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TopUpHistoryModal;
